import { useRef, useState } from 'react'
import {
    Alert,
    AlertDescription,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Avatar,
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    SimpleGrid,
    Stack,
    Text,
    useDisclosure
} from '@chakra-ui/react'
import { FiCheck, FiSlash, FiX } from 'react-icons/fi'
import { useUserTypes } from '@/shared/hooks/useUserTypes'
import { UserTypes } from '@/shared/types/auth'
import {
    RegistryTeacher,
    STATUS_COLOR,
    STATUS_LABEL,
    getTeacherName,
    normalizeStatus
} from '../types'
import { useTeacherActions } from '../hooks/useTeacherActions'

type Props = { teacher: RegistryTeacher | null }

type ConfirmAction = {
    title: string
    body: string
    confirmLabel: string
    colorScheme: string
    run: () => Promise<unknown>
}

const Field = ({ label, value }: { label: string; value?: string | null }) => (
    <Box>
        <Text fontSize="xs" textTransform="uppercase" color="gray.500">
            {label}
        </Text>
        <Text fontSize="sm">{value?.trim() ? value : '—'}</Text>
    </Box>
)

const formatDate = (value?: string | null) => {
    if (!value) return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString()
}

export default function TeacherDetails({ teacher }: Props) {
    const { approve, reject, suspend, pending } = useTeacherActions()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [confirm, setConfirm] = useState<ConfirmAction | null>(null)
    const cancelRef = useRef<HTMLButtonElement>(null)
    // POST /teachers/:id/suspend is guarded by @Perms([SUDO]) alone — unlike
    // approve/reject, an admin teacher may not call it and would get a 403.
    const canSuspend = useUserTypes().hasType(UserTypes.IS_GLOBAL_ADMIN)

    if (!teacher) {
        return (
            <Flex justify="center" align="center" h="full" minH="240px" p={6}>
                <Text color="gray.500">Select a teacher to view details.</Text>
            </Flex>
        )
    }

    const name = getTeacherName(teacher)
    const status = normalizeStatus(teacher.status)
    const isPending = status === 'pending'
    // An admin teacher looking at an approved colleague has nothing to do here,
    // so the whole block collapses rather than showing an empty heading.
    const hasActions = status !== 'active' || canSuspend

    const ask = (action: ConfirmAction) => {
        setConfirm(action)
        onOpen()
    }

    const runConfirm = async () => {
        if (!confirm) return
        try {
            await confirm.run()
        } catch {
            // useTeacherActions surfaces the failure as an error toast.
        }
        onClose()
    }

    return (
        <Box p={{ base: 4, md: 5 }}>
            <Flex justify="space-between" align="start" flexWrap="wrap" gap={4}>
                <Flex gap={4} flex="1" minW={{ base: 'full', md: '320px' }}>
                    <Avatar
                        size="lg"
                        name={name}
                        src={teacher.picture ?? undefined}
                        flexShrink={0}
                    />
                    <Box minW={0}>
                        <Heading size="md">{name}</Heading>
                        <Text color="gray.600" fontSize="sm">
                            {teacher.branch?.name || 'No branch'}
                        </Text>
                    </Box>
                </Flex>
                <Stack align={{ base: 'start', sm: 'end' }} spacing={2}>
                    <Badge
                        colorScheme={STATUS_COLOR[status]}
                        borderRadius="full"
                        px={3}
                        py={1}
                    >
                        {STATUS_LABEL[status]}
                    </Badge>
                    {teacher.isAdmin ? (
                        <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                            Admin teacher
                        </Badge>
                    ) : null}
                </Stack>
            </Flex>

            {isPending ? (
                <Alert status="warning" borderRadius="md" mt={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        This teacher is awaiting approval for{' '}
                        {teacher.branch?.name || 'their branch'}.
                    </AlertDescription>
                </Alert>
            ) : null}

            <SimpleGrid
                columns={{ base: 1, sm: 2 }}
                spacingX={6}
                spacingY={3}
                mt={5}
                maxW="xl"
            >
                <Field label="Email" value={teacher.email} />
                <Field label="Phone" value={teacher.phoneNumber} />
                <Field label="Branch" value={teacher.branch?.name} />
                <Field label="Requested" value={formatDate(teacher.createdAt)} />
                {status !== 'pending' ? (
                    <Field label="Approved" value={formatDate(teacher.approvedAt)} />
                ) : null}
            </SimpleGrid>

            {hasActions ? (
                <Box mt={6} pt={4} borderTopWidth="1px" borderColor="gray.100">
                    <Heading size="sm" mb={3}>
                        Actions
                    </Heading>
                    <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
                        {status !== 'active' ? (
                            <Button
                                size="sm"
                                colorScheme="green"
                                leftIcon={<FiCheck />}
                                isDisabled={pending}
                                onClick={() =>
                                    ask({
                                        title: isPending
                                            ? 'Approve teacher'
                                            : 'Reinstate teacher',
                                        body: `Grant ${name} teacher access${
                                            teacher.branch?.name
                                                ? ` for ${teacher.branch.name}`
                                                : ''
                                        }?`,
                                        confirmLabel: isPending ? 'Approve' : 'Reinstate',
                                        colorScheme: 'green',
                                        run: () => approve(teacher._id)
                                    })
                                }
                            >
                                {isPending ? 'Approve' : 'Reinstate'}
                            </Button>
                        ) : null}

                        {isPending ? (
                            <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FiX />}
                                isDisabled={pending}
                                onClick={() =>
                                    ask({
                                        title: 'Reject request',
                                        body: `Reject ${name}'s request to become a teacher? They will need to request the role again.`,
                                        confirmLabel: 'Reject',
                                        colorScheme: 'red',
                                        run: () => reject(teacher._id)
                                    })
                                }
                            >
                                Reject
                            </Button>
                        ) : null}

                        {status === 'active' && canSuspend ? (
                            <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FiSlash />}
                                isDisabled={pending}
                                onClick={() =>
                                    ask({
                                        title: 'Revoke access',
                                        body: `Revoke ${name}'s teacher access? They will no longer be able to check children in or out.`,
                                        confirmLabel: 'Revoke access',
                                        colorScheme: 'red',
                                        run: () => suspend(teacher._id)
                                    })
                                }
                            >
                                Revoke access
                            </Button>
                        ) : null}
                    </Stack>
                </Box>
            ) : null}

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {confirm?.title}
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">{confirm?.body}</AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button
                                ref={cancelRef}
                                size="sm"
                                variant="ghost"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                colorScheme={confirm?.colorScheme}
                                isLoading={pending}
                                onClick={runConfirm}
                            >
                                {confirm?.confirmLabel}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    )
}
